<script setup lang="ts">
import { onMounted, ref, type PropType } from 'vue';
import type { SelectOption } from 'naive-ui';
import GaiaAutoComplete from '@/components/design/base/inputs/GaiaAutoComplete.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import GaiaTagInput from '@/components/design/base/inputs/GaiaTagInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaRemoteSelect from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import { logger } from '@/utils/logger';
import { toText, type EntityField } from './entityListConfig';

/**
 * The control for one `EntityField`, by its `kind` — shared by the create /
 * edit drawer and the detail page's inline editor so both draw a field the
 * same way. A field's `loadOptions` list is fetched here, on mount.
 */
const props = defineProps({
  field: { type: Object as PropType<EntityField>, required: true },
  /** A `tags` / `multiselect` field holds an array; everything else a string. */
  value: { type: [String, Array] as PropType<string | string[]>, required: true },
  error: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  /** Labels for a `searchOptions` field's current values; a plain select's options. */
  seedOptions: { type: Array as PropType<SelectOption[]>, default: () => [] },
});

const emit = defineEmits<{ 'update:value': [value: string | string[]] }>();

const loadedOptions = ref<SelectOption[]>([]);

onMounted(async () => {
  if (!props.field.loadOptions) return;
  try {
    loadedOptions.value = await props.field.loadOptions();
  } catch (error) {
    logger.error('Error loading field options:', error);
  }
});

const text = (): string => toText(props.value);
const list = (): string[] => (Array.isArray(props.value) ? props.value : []);
const status = (): 'error' | undefined => (props.error ? 'error' : undefined);

function update(value: string | string[]): void {
  emit(
    'update:value',
    typeof value === 'string' && props.field.upper ? value.toUpperCase() : value
  );
}
</script>

<template>
  <!-- Loaded options (countries) are long, so they filter; an optional select
       clears back to empty. -->
  <gaia-select
    v-if="field.kind === 'select'"
    :value="text() || null"
    :options="field.options ?? (field.loadOptions ? loadedOptions : seedOptions)"
    :placeholder="field.placeholder"
    :filterable="Boolean(field.loadOptions)"
    :clearable="!field.required"
    :disabled="disabled"
    size="medium"
    :status="status()"
    style="width: 100%"
    @update:value="(value: string | null) => update(value ?? '')"
  />
  <gaia-remote-select
    v-else-if="field.kind === 'multiselect' && field.searchOptions"
    multiple
    clearable
    :fetch="field.searchOptions"
    :seed-options="seedOptions"
    :value="list()"
    :placeholder="field.placeholder"
    size="medium"
    :disabled="disabled"
    style="width: 100%"
    @update:value="(value: string[]) => update(value)"
  />
  <gaia-select
    v-else-if="field.kind === 'multiselect'"
    multiple
    filterable
    :value="list()"
    :options="loadedOptions"
    :placeholder="field.placeholder"
    size="medium"
    :disabled="disabled"
    style="width: 100%"
    @update:value="(value: string[]) => update(value)"
  />
  <gaia-textarea
    v-else-if="field.kind === 'textarea'"
    :value="text()"
    :placeholder="field.placeholder"
    :autosize="{ minRows: field.minRows ?? 3 }"
    :status="status()"
    :disabled="disabled"
    style="width: 100%"
    @update:value="(value: string) => update(value)"
  />
  <gaia-tag-input
    v-else-if="field.kind === 'tags'"
    :value="list()"
    :placeholder="field.placeholder"
    :disabled="disabled"
    style="width: 100%"
    @update:value="(value: string[]) => update(value)"
  />
  <gaia-date-picker
    v-else-if="field.kind === 'date'"
    :formatted-value="text() || null"
    value-format="yyyy-MM-dd"
    type="date"
    size="medium"
    :status="status()"
    :disabled="disabled"
    style="width: 100%"
    @update:formatted-value="(value: string | null) => update(value ?? '')"
  />
  <gaia-input-number
    v-else-if="field.kind === 'number'"
    :value="text() === '' ? null : Number(text())"
    :min="1"
    :precision="0"
    size="medium"
    :placeholder="field.placeholder"
    :status="status()"
    style="width: 100%"
    @update:value="(value: number | null) => update(value === null ? '' : String(value))"
  />
  <gaia-auto-complete
    v-else-if="field.suggestions"
    :value="text()"
    :suggestions="field.suggestions"
    size="medium"
    :placeholder="field.placeholder"
    :status="status()"
    :disabled="disabled"
    style="width: 100%"
    @update:value="(value: string) => update(value)"
  />
  <gaia-input
    v-else
    :value="text()"
    size="medium"
    :placeholder="field.placeholder"
    :maxlength="field.maxLength"
    :status="status()"
    :input-props="{ style: field.upper ? 'text-transform: uppercase' : undefined }"
    :disabled="disabled"
    style="width: 100%"
    @update:value="(value: string) => update(value)"
  />
</template>
